import { bootstrapApplication } from "@angular/platform-browser";
import { Capacitor } from "@capacitor/core";
import { AppComponent } from "./app/app.component";

import { appProviders } from "./app/providers/appProviders";
import { webProviders } from "./app/providers/webProviders";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { jwtInterceptor } from "./app/token.interceptor";
import {
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
} from "@angular/core";
import { AppShellService } from "./app/services/app-shell.service";
import { ThemeService } from "./app/services/theme.service";
import { AuthService } from "./app/services/auth.service";
import { SessionService } from "./app/services/session.service";
import { SelectedTripUrlService } from "./app/services/selected-trip-url/selected-trip-url.service";
import { GlLegalGateRedirectService } from "./app/services/gl-legal-gate-redirect.service";
import { TranslocoHttpLoader } from "./transloco-loader";
import { provideTransloco } from "@jsverse/transloco";
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from "@ionic/angular";
import {
  PreloadAllModules,
  provideRouter,
  RouteReuseStrategy,
  withPreloading,
  withRouterConfig,
  withViewTransitions,
} from "@angular/router";
import { routes } from "./app/app.routes";
import { IonicStorageModule } from "@ionic/storage-angular";
import { provideServiceWorker } from "@angular/service-worker";
import { provideCharts, withDefaultRegisterables } from "ng2-charts";
import { environment } from "./environments/environment";
import { PwaUpdateService } from "./app/services/pwa-update/pwa-update.service";
import { ChatlioService } from "./app/services/support-chat/chatlio.service";
import { LanguagePreferenceService } from "./app/services/language-preference.service";
import {
  installTrackjs,
  toTrackjsPlatform,
} from "./app/services/trackjs/trackjs.config";
import { TrackjsErrorHandler } from "./app/services/trackjs/trackjs-error-handler";
import { TrackjsIdentityService } from "./app/services/trackjs/trackjs-identity.service";

installTrackjs(toTrackjsPlatform(Capacitor.getPlatform()), window.location.hostname);

bootstrapApplication(AppComponent, {
  providers: [
    // Do not add provideBrowserGlobalErrorListeners(): TrackJS already listens on window
    // for errors and unhandled rejections (ADR-0041). Angular's listeners would forward the
    // same events to TrackjsErrorHandler and every async error would be reported twice.
    { provide: ErrorHandler, useClass: TrackjsErrorHandler },
    provideAppInitializer(() => {
      const themeService = inject(ThemeService);
      const appShellService = inject(AppShellService);
      appShellService.initialize();
      return themeService.initializeTheme();
    }),
    provideAppInitializer(() => {
      const authService = inject(AuthService);
      const sessionService = inject(SessionService);
      const languagePreferenceService = inject(LanguagePreferenceService);
      return (async (): Promise<void> => {
        await authService.restoreSessionFromRefreshCookie();
        await sessionService.hydrate();
        await languagePreferenceService.initializeFromServer();
      })();
    }),
    provideAppInitializer(() => {
      inject(SelectedTripUrlService);
      inject(GlLegalGateRedirectService);
      inject(ChatlioService).initialize();
      inject(TrackjsIdentityService).initialize();
    }),
    ...appProviders,
    ...webProviders,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({
      animated: true,
      useSetInputAPI: true,
    }),
    provideRouter(
      routes,
      withRouterConfig({ onSameUrlNavigation: "reload" }),
      withPreloading(PreloadAllModules),
      withViewTransitions()
    ),
    importProvidersFrom(IonicStorageModule.forRoot()),
    ...(environment.serviceWorker
      ? [
          provideServiceWorker("ngsw-worker.js", {
            enabled: true,
            registrationStrategy: "registerWhenStable:30000",
          }),
          provideAppInitializer(() => {
            inject(PwaUpdateService).initialize();
          }),
        ]
      : []),
    provideHttpClient(withInterceptors([jwtInterceptor])),
    provideTransloco({
      config: {
        availableLangs: ["en", "es"],
        defaultLang: "en",
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideCharts(withDefaultRegisterables()),
  ],
});
